import axios from "axios";
import { axiosClient } from "./axios-client";
import type {
  AuthResponse,
  Enable2FA,
  Estado2FA,
  LoginChallenge,
  LoginRequest,
  Setup2FA,
} from "@/lib/types";

/**
 * Endpoints de autenticación · login (sin token, axios crudo), checkReady
 * (con interceptor), logout (best-effort).
 *
 * Vive en `shared/api/` porque la sesión es cross-feature · no pertenece
 * a una feature específica (la usa el form de login, el AppShell para
 * logout, el dashboard para healthcheck).
 */

// En Vite las env vars van por `import.meta.env.VITE_*` · si no está
// definida usamos paths relativos para que el proxy del dev-server (y
// nginx en prod) maneje la conexión al backend .NET.
const baseURL =
  (import.meta.env.VITE_API_URL as string | undefined) ?? "";

export async function login(req: LoginRequest): Promise<AuthResponse | LoginChallenge> {
  // Login NO usa el axios con interceptor · todavía no hay token, así que
  // la versión del API (P32) va explícita en el path. Con 2FA activo (P36)
  // la respuesta es un reto en lugar de los tokens.
  const { data } = await axios.post<AuthResponse | LoginChallenge>(
    `${baseURL}/api/v1/auth/login`,
    req,
    { headers: { "Content-Type": "application/json" } }
  );
  return data;
}

/**
 * Solicita el enlace de recuperación de contraseña. El backend SIEMPRE
 * responde 204 (anti-enumeración) · el mensaje al usuario debe ser neutro.
 */
export async function forgotPassword(email: string): Promise<void> {
  await axios.post(
    `${baseURL}/api/v1/auth/forgot-password`,
    { email },
    { headers: { "Content-Type": "application/json" } }
  );
}

/** Restablece la contraseña con el token del correo de recuperación. */
export async function resetPassword(args: {
  email: string;
  token: string;
  newPassword: string;
}): Promise<void> {
  await axios.post(
    `${baseURL}/api/v1/auth/reset-password`,
    args,
    { headers: { "Content-Type": "application/json" } }
  );
}

/** True si el login respondió el reto 2FA en lugar de los tokens (P36). */
export function esRetoDosFactores(r: AuthResponse | LoginChallenge): r is LoginChallenge {
  return "requiresTwoFactor" in r && r.requiresTwoFactor === true;
}

/** Segundo paso del login (P36) · token intermedio + código TOTP o de recuperación. */
export async function login2FA(args: {
  email: string;
  twoFactorToken: string;
  codigo: string;
  esCodigoRecuperacion?: boolean;
}): Promise<AuthResponse> {
  const { data } = await axios.post<AuthResponse>(
    `${baseURL}/api/v1/auth/login/2fa`,
    args,
    { headers: { "Content-Type": "application/json" } }
  );
  return data;
}

// --- Gestión del segundo factor (usuario autenticado · página Seguridad) ----

export async function estado2FA(): Promise<Estado2FA> {
  const { data } = await axiosClient.get<Estado2FA>("/api/auth/2fa");
  return data;
}

export async function setup2FA(): Promise<Setup2FA> {
  const { data } = await axiosClient.post<Setup2FA>("/api/auth/2fa/setup");
  return data;
}

export async function enable2FA(codigo: string): Promise<Enable2FA> {
  const { data } = await axiosClient.post<Enable2FA>("/api/auth/2fa/enable", { codigo });
  return data;
}

export async function disable2FA(codigo: string): Promise<void> {
  await axiosClient.post("/api/auth/2fa/disable", { codigo });
}

export async function checkReady(): Promise<{ status: string }> {
  const { data } = await axiosClient.get<{ status: string }>(
    "/api/health/ready"
  );
  return data;
}

/**
 * Cierra la sesión en backend · revoca el refresh token y registra
 * la auditoría. Best-effort: si falla la red o el endpoint, igual
 * limpiamos el state local en el caller.
 */
export async function logout(refreshToken: string | null): Promise<void> {
  try {
    await axiosClient.post("/api/auth/logout", { refreshToken });
  } catch {
    // Idempotente · si el access token ya expiró, ignoramos.
  }
}
